/**
 * SearchPage Component
 * Main search page integrating SearchForm, ResultsTable, and RateLimitBanner
 */

import React, { useState, useEffect } from 'react';
import SearchForm from '../SearchForm';
import ResultsTable from '../ResultsTable';
import RateLimitBanner from '../RateLimitBanner';
import { useQuery } from '@tanstack/react-query';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../lib/AuthContext';
import { usePageMeta } from '../../lib/usePageMeta';
import type { SearchParams, SearchResponse, RateLimitInfo } from '../types/api.types';

const SEARCH_API_URL = import.meta.env.VITE_API_URL || '/api';

const SearchPage: React.FC = () => {
  const { user } = useAuth();
  usePageMeta({ title: 'Search Professionals', description: 'Search our database of licensed professionals by state, city, profession, or ZIP code.', path: '/search' });
  const searchParams = useSearch({ from: '/search' }) as Partial<SearchParams>;
  const activeParams = searchParams as unknown as SearchParams;
  const [showBanner, setShowBanner] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo>({
    searches_used: 0,
    searches_limit: 10,
    searches_remaining: 10,
    tier: 'free',
  });

  const navigate = useNavigate({ from: '/search' });

  // Query Implementation
  const { data, isLoading, error, isRefetching } = useQuery<SearchResponse, Error>({
    queryKey: ['search', searchParams],
    queryFn: async (): Promise<SearchResponse> => {
      // Throw error or return empty structure if no state (to satisfy type)
      if (!searchParams.state) {
        throw new Error('No search state provided');
      }

      const token = localStorage.getItem('progeodata_token') || (user as any)?.token || '';

      const res = await fetch(`${SEARCH_API_URL}/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(searchParams),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({})) as any;
        if (res.status === 401) throw new Error('Please log in to search');
        if (res.status === 402) {
          const msg = errData.upgrade_prompt || errData.error || 'Insufficient credits. Please upgrade your plan.';
          throw new Error(msg);
        }
        throw new Error(errData.error || 'Search failed');
      }
      return res.json();
    },
    enabled: !!searchParams.state,
    staleTime: 1000 * 60 * 5, // 5 min cache
    retry: false
  });

  // Effect to update rate limit
  useEffect(() => {
    if (data) {
      const usedIncrement = data.free_tier ? 1 : 1;
      setRateLimitInfo(prev => ({
        ...prev,
        searches_used: prev.searches_used + usedIncrement,
        searches_remaining: data.free_tier
          ? data.free_tier.lookups_remaining ?? prev.searches_remaining - 1
          : data.credits_remaining ?? prev.searches_remaining
      }));

      if (data.free_tier && data.free_tier.lookups_remaining <= 0) {
        setShowBanner(true);
      } else if (data.credits_remaining !== undefined && data.credits_remaining <= 0) {
        setShowBanner(true);
      }
    }
  }, [data]);

  const handleSearch = (params: SearchParams) => {
    if (rateLimitInfo.searches_remaining <= 0 && rateLimitInfo.tier === 'free') {
      setShowBanner(true);
      return;
    }
    navigate({ search: params });
  };

  const handleExportCsv = () => {
    if (!data?.results) return;

    const headers = ['Name', 'License #', 'Status', 'Location', 'Brokerage', 'Email', 'Phone'];
    const rows = data.results.map((prof) => [
      prof.name,
      prof.license_number,
      prof.status,
      prof.city && prof.state ? `${prof.city}, ${prof.state}` : prof.state,
      prof.brokerage || 'N/A',
      prof.email || '',
      prof.phone || '',
    ]);

    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `progeodata-search-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-slate-900 py-12">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Professional License Verification",
          "provider": {
            "@type": "Organization",
            "name": "ProGeoData"
          },
          "description": "Search and verify licensed professionals across the United States. Check active license status, location, and contact information.",
          "serviceType": "License Verification"
        }) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://progeodata.com/" },
            { "@type": "ListItem", "position": 2, "name": "Search", "item": "https://progeodata.com/search" }
          ]
        }) }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            Professional License Search
          </h1>
          <p className="text-lg text-slate-400">
            Search for licensed professionals across multiple states and industries
          </p>
        </div>

        {/* Rate Limit Banner */}
        {showBanner && (
          <RateLimitBanner
            searchesUsed={rateLimitInfo.searches_used}
            searchesLimit={rateLimitInfo.searches_limit}
            tier={rateLimitInfo.tier}
            onDismiss={() => setShowBanner(false)}
          />
        )}

        {/* Search Form */}
        <div className="mb-8">
          <SearchForm
            onSearch={handleSearch}
            isLoading={isLoading || isRefetching}
            rateLimitInfo={rateLimitInfo}
          />
        </div>

        {/* Loading State */}
        {(isLoading || isRefetching) && (
          <div className="bg-sky-900/20 border border-sky-700 rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-4">
              <div className="animate-spin h-6 w-6 text-sky-500 flex-shrink-0 mt-1">
                <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <div>
                <p className="text-lg font-semibold text-sky-400 mb-2">
                  Searching {activeParams?.state || '...'} licensing board...
                </p>
                <p className="text-sm text-slate-400">
                  <strong>Note:</strong> First searches take 3-5 seconds while we scrape live data.
                  Repeat searches are instant from cache.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-4">
              <div>
                <h3 className="text-lg font-bold text-red-400 mb-1">Search Error</h3>
                <p className="text-slate-300">{error.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Table */}
        {data && !error && (
          <ResultsTable
            results={data.results}
            isLoading={isLoading || isRefetching}
            onExportCsv={handleExportCsv}
            canExport={rateLimitInfo.tier !== 'free'}
            tier={rateLimitInfo.tier}
          />
        )}

        {/* Help Text */}
        <div className="mt-8 bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-bold text-slate-200 mb-4">How to Use</h3>
          <div className="space-y-3 text-slate-400">
            <p>1. Select a state.</p>
            <p>2. Choose a profession.</p>
            <p>3. Enter ZIP (optional).</p>
            <p>4. Click Search.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
